// standards-ignore: repeated ' gridGap=' is a Stencil layout prop, not a magic string
import React from 'react';
import { Row, View } from '@amzn/stencil-react-components/layout';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { Text } from '@amzn/stencil-react-components/text';
import { IconCheckSmall } from '@amzn/stencil-react-icons';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

/**
 * Save status enum-like object. Use these named constants instead of
 * passing string literals so consumers and the indicator itself agree
 * on the exact values, and a typo becomes a compile error.
 */
export const SaveStatus = {
  Idle: 'idle',
  Saving: 'saving',
  Saved: 'saved',
  Error: 'error',
} as const;

export type SaveStatus = (typeof SaveStatus)[keyof typeof SaveStatus];

export interface SaveIndicatorProps {
  /** Current save state. `Idle` and `Error` render nothing. */
  readonly status: SaveStatus;
  /** Localized label shown next to the spinner while saving. Also used
   *  as the `aria-label` + `title` in compact mode. */
  readonly savingLabel: string;
  /** Localized label shown next to the check icon when saved. Also
   *  used as the `aria-label` + `title` in compact mode. */
  readonly savedLabel: string;
  /**
   * When true, the indicator renders as ICON ONLY (spinner or check)
   * with `savingLabel` / `savedLabel` mapped to `aria-label` and
   * `title` so assistive tech and hover tooltips still announce the
   * state.
   *
   * Use in tight horizontal spaces — e.g. a mobile row where the
   * indicator shares the line with three answer buttons. The text
   * label pushes the buttons off-screen on ~360px viewports; the
   * icon alone is ~20px wide, is universally understood
   * (spinner = in-flight, green check = confirmed), and screen
   * readers still get the full label via aria.
   *
   * Default: false (preserves the existing icon + text shape for
   * desktop consumers).
   */
  readonly compact?: boolean;
}

/**
 * Inline "saving / saved" indicator commonly placed next to a page title
 * for autosaved forms. Errors are intentionally not surfaced here — pair
 * with a `MessageBanner` in the page so the inline indicator stays terse.
 *
 * Renders in two shapes controlled by {@link SaveIndicatorProps.compact}:
 *
 *   - Default: `[icon] [label]` in a Row. Suitable for wide surfaces
 *     like a page header where the label doubles as visual affordance.
 *   - Compact: icon only, label mapped to `aria-label` + `title`.
 *     Suitable for narrow surfaces (mobile row + button cluster) where
 *     the label would overflow horizontally.
 */
export function SaveIndicator({
  status,
  savingLabel,
  savedLabel,
  compact = false,
}: SaveIndicatorProps): React.ReactElement | null {
  if (status === SaveStatus.Saving) {
    if (compact) {
      // Role=status + aria-live=polite so the "Saving..." transition
      // is announced without stealing focus. Title provides a hover
      // tooltip on desktop-inspection; on touch, sighted users
      // recognise the spinner glyph without the text.
      return (
        <View
          role="status"
          aria-label={savingLabel}
          aria-live="polite"
          title={savingLabel}
          data-testid="save-indicator-saving"
        >
          <Spinner size={SpinnerSize.ExtraSmall} />
        </View>
      );
    }
    return (
      <Row alignItems="center" gridGap="dimensions.spacing.100">
        <Spinner size={SpinnerSize.ExtraSmall} />
        <Text color={token('color.text.secondary')}>{savingLabel}</Text>
      </Row>
    );
  }
  if (status === SaveStatus.Saved) {
    // Saved uses Stencil's `color.status.success-icon` for both the
    // check and the label plus bold weight so the momentary
    // confirmation reads as a positive event rather than fading into
    // secondary body copy. Stencil doesn't ship a `success-text`
    // variant; `success-icon` is the semantic success accent and works
    // for both the SVG and the adjacent text. Icon accepts the token
    // KEY string directly, matching how AuditCard/ListCard pass status
    // colors elsewhere in v2.
    if (compact) {
      return (
        <View
          role="status"
          aria-label={savedLabel}
          aria-live="polite"
          title={savedLabel}
          data-testid="save-indicator-saved"
        >
          <IconCheckSmall aria-hidden color="color.status.success-icon" />
        </View>
      );
    }
    return (
      <Row alignItems="center" gridGap="dimensions.spacing.100">
        <IconCheckSmall aria-hidden color="color.status.success-icon" />
        <Text color={token('color.status.success-icon')} fontWeight="bold">
          {savedLabel}
        </Text>
      </Row>
    );
  }
  return null;
}
